import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import './TaskSubtasks.css';

const CHAT_API_BASE = '/api/chat';

// Authenticated fetch wrapper - adds Bearer token to all requests.
const authFetch = async (url, options = {}) => {
  const token = await api.ensureAuthenticated();
  const headers = { ...(options.headers || {}), 'Authorization': `Bearer ${token}` };
  return fetch(url, { ...options, headers });
};

// Subtasks of a task. A subtask is a full task parented to this one (it carries
// its own status/priority/assignee/notes/conversation and can even have its own
// subtasks); clicking one opens it via onOpenSubtask. Backed by project-tasks.php
// (?parent_task_id= to list, POST with parent_task_id to create, DELETE to remove).
const TaskSubtasks = ({ parentTask, currentUser, taskUsers = [], onOpenSubtask, onChanged }) => {
  const parentId = parentTask?.id;
  const [subtasks, setSubtasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [assignee, setAssignee] = useState('');
  const [priority, setPriority] = useState('Medium');
  const [dueDate, setDueDate] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!parentId) return;
    setLoading(true);
    try {
      const res = await authFetch(`${CHAT_API_BASE}/project-tasks.php?parent_task_id=${encodeURIComponent(parentId)}`);
      const data = await res.json();
      if (data.success) setSubtasks(data.data || []);
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, [parentId]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => { setName(''); setAssignee(''); setPriority('Medium'); setDueDate(''); };

  const addSubtask = async () => {
    const n = name.trim();
    if (!n) return;
    setBusy(true);
    try {
      const res = await authFetch(`${CHAT_API_BASE}/project-tasks.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          parent_task_id: parentId,
          name: n,
          created_by: currentUser?.id,
          assigned_user_id: assignee || undefined,
          priority,
          date_due: dueDate ? dueDate + ' 00:00:00' : undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        resetForm();
        setAdding(false);
        load();
        if (onChanged) onChanged();
      } else {
        alert(data.message || 'Failed to add subtask');
      }
    } catch (e) {
      alert('Failed to add subtask: ' + e.message);
    }
    setBusy(false);
  };

  const removeSubtask = async (e, st) => {
    e.stopPropagation();
    if (!window.confirm('Delete this subtask? Responses on it will remain in the record.')) return;
    setBusy(true);
    try {
      const res = await authFetch(`${CHAT_API_BASE}/project-tasks.php?id=${encodeURIComponent(st.id)}&user_id=${encodeURIComponent(currentUser?.id || '')}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) { load(); if (onChanged) onChanged(); }
      else alert(data.message || 'Failed to delete subtask');
    } catch (e2) {
      alert('Failed to delete subtask: ' + e2.message);
    }
    setBusy(false);
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '';

  return (
    <div className="task-subtasks">
      <div className="task-subtasks-header">
        <h3 className="task-detail-notes-title" style={{ margin: 0 }}>
          Subtasks{subtasks.length ? ` (${subtasks.length})` : ''}
        </h3>
        <button className="task-conv-btn" onClick={() => { setAdding(a => !a); if (adding) resetForm(); }} disabled={busy}>
          {adding ? 'Cancel' : '+ Add subtask'}
        </button>
      </div>

      {adding && (
        <div className="task-subtask-add">
          <input
            type="text"
            className="task-subtask-add-name"
            placeholder="Subtask name"
            value={name}
            autoFocus
            onChange={e => setName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addSubtask(); } }}
          />
          <select value={assignee} onChange={e => setAssignee(e.target.value)}>
            <option value="">Assignee…</option>
            {/* coworkers.php returns `user_id`; users.php returns `id` — tolerate both. */}
            {taskUsers.filter(u => (u.user_id || u.id)).map(u => {
              const uid = u.user_id || u.id;
              const uname = u.name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.user_name || u.username || '';
              return <option key={uid} value={uid}>{uname}</option>;
            })}
          </select>
          <select value={priority} onChange={e => setPriority(e.target.value)}>
            {['High', 'Medium', 'Low'].map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} title="Due date" />
          <button className="task-conv-send-btn" onClick={addSubtask} disabled={busy || !name.trim()}>
            {busy ? '…' : 'Add'}
          </button>
        </div>
      )}

      {loading ? (
        <div className="task-subtasks-empty">Loading…</div>
      ) : subtasks.length === 0 ? (
        <div className="task-subtasks-empty">No subtasks yet.</div>
      ) : (
        <div className="task-subtask-list">
          {subtasks.map(st => {
            const a = st.attributes || {};
            const canDelete = currentUser && currentUser.id === a.created_by;
            return (
              <div
                key={st.id}
                className="task-subtask-row"
                onClick={() => onOpenSubtask && onOpenSubtask(st)}
                title="Open subtask"
              >
                <span className="task-subtask-name">{a.name || 'Untitled'}</span>
                <span className={`task-status-badge status-${(a.status || 'no-status').toLowerCase().replace(/\s+/g, '-')}`}>
                  {a.status || 'No Status'}
                </span>
                <span className={`task-priority-badge priority-${(a.priority || 'none').toLowerCase().replace(/\s+/g, '-')}`}>
                  {a.priority || 'No Priority'}
                </span>
                <span className="task-subtask-assignee">@{(a.assigned_user_name || '').trim() || 'Unassigned'}</span>
                {a.date_due && <span className="task-subtask-due">Due {fmtDate(a.date_due)}</span>}
                {Number(a.sub_count) > 0 && <span className="task-subtask-count">{a.sub_count} sub</span>}
                <span className="task-subtask-spacer" />
                {canDelete && (
                  <button
                    className="task-conv-btn task-conv-btn-danger"
                    onClick={(e) => removeSubtask(e, st)}
                    disabled={busy}
                  >
                    Delete
                  </button>
                )}
                <span className="task-subtask-open">›</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TaskSubtasks;
